import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { NavBar, NavBarRef } from '../../components/navBar.tsx';
import { PageTitle } from '../../components/pageTitle.tsx';
import { ProgramNameMappingDialog } from '../../components/program-name-mapping/programNameMappingDialog.tsx';
import { useLoadMoreOnScroll } from '../../hooks/useLoadMoreOnScroll.ts';
import { Protocol } from '../../types/sharedTypeImpl.ts';
import { encodeUrl } from '../../utils/encodeUrl.ts';
import { trpc, trpcClient } from '../../utils/trpc/trpc.ts';
import { ProtocolListInputs } from './protocolListInputs.tsx';
import { ProtocolTable } from './protocolTable.tsx';
import { useProtocolFilters } from './useProtocolFilters.ts';


export function ProtocolList() {
    const [showMappingsDialog, setShowMappingsDialog] = useState(false);
    const navBarRef = useRef<NavBarRef>(null);

    const navigate = useNavigate();

    const {
        searchTextInput,
        searchText,
        setSearchTextDebounced,
        setSearchTextImmediate,
        onlyWithInterval,
        setOnlyWithInterval,
        showDisabled,
        setShowDisabled,
        handleClearFilters,
        status,
        setStatus,
    } = useProtocolFilters();


    const protocolsQuery = trpc.protocols.getInfinite.useInfiniteQuery(
        {
            search: searchText,
            isDisabled: showDisabled ? undefined : false,
            status: status ? status : undefined,
            onlyWithInterval: onlyWithInterval ? true : undefined,
            limit: 50,
        },
        {
            getNextPageParam: (last) => last.nextCursor ?? null,
            placeholderData: (prev) => prev,// Prevents flickering when editing filters
        },
    );

    useLoadMoreOnScroll(protocolsQuery);


    const handleSetEnabled = async (protocol: Protocol, isDisabled: boolean) => {
        const success = await trpcClient.protocols.setDisabled.mutate({
            protocolId: protocol._id!,
            isDisabled,
        });
        if (success) {
            protocolsQuery.refetch();
            navBarRef.current?.refetchPageIssues();
        } else {
            alert('Failed to set enabled status');
        }
    }

    const handleDraftDelete = async (protocol: Protocol) => {
        const partName = protocol.partDisplayName ? `"${protocol.partDisplayName}"` : 'unnamed part';
        const confirm = window.confirm(`Delete draft for ${partName}?`);
        if (!confirm) return;
        try {
            const success = await trpcClient.protocols.deleteDraft.mutate(protocol._id!);
            if (success) {
                protocolsQuery.refetch();
            } else {
                alert('Failed to delete draft.');
            }
        } catch (error) {
            alert('Failed to delete draft.');
        }
    }

    const handleProtocolCopy = (protocol: Protocol) => {
        const returnTo = { returnTo: location.pathname + location.search };
        const path = encodeUrl('/protocol-view', { baseProtocolId: protocol._id, isCopy: true, mode: 'edit' });
        navigate(path, { state: returnTo });
    }

    const handleShowMappingsClick = () => {
        setShowMappingsDialog(true);
    }


    const protocols = protocolsQuery.data?.pages.flatMap(page => page.protocols) ?? [];
    return (
        <>
            <PageTitle title='Protocols' />
            <NavBar ref={navBarRef} />
            <div className='content' style={{ width: '1450px' }}>
                <ProtocolListInputs
                    isLoading={protocolsQuery.isLoading}
                    error={protocolsQuery.error}
                    showDisabled={showDisabled}
                    onlyWithInterval={onlyWithInterval}
                    filterInput={searchTextInput}
                    status={status}
                    setStatus={setStatus}
                    setShowDisabled={setShowDisabled}
                    setOnlyWithInterval={setOnlyWithInterval}
                    setFilterInput={setSearchTextDebounced}
                    onShowMappingsClick={handleShowMappingsClick}
                    handleClearFilters={handleClearFilters}
                />
                <ProtocolTable
                    protocols={protocols}
                    onProtocolSetDisabled={handleSetEnabled}
                    onProtocolCopy={handleProtocolCopy}
                    onProtocolDraftDelete={handleDraftDelete}
                    onSearchTextChange={setSearchTextImmediate}
                />

                {protocolsQuery.hasNextPage && (
                    <div className='list--loading-more'>Loading…</div>
                )}
            </div>

            {showMappingsDialog &&
                <ProgramNameMappingDialog onClose={() => setShowMappingsDialog(false)} />
            }
        </>
    );
}
